<script>
import { ref, provide, useTemplateRef } from 'vue';
import { useMessageSelection } from 'dashboard/composables/useMessageSelection.js';
import ForwardMessageModal from 'dashboard/modules/conversations/components/ForwardMessageModal.vue';
import { useElementSize } from '@vueuse/core';
// composable
import { useAlert } from 'dashboard/composables';
import { useLabelSuggestions } from 'dashboard/composables/useLabelSuggestions';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';

// components
import ReplyBox from './ReplyBox.vue';
import ConversationPinnedMessage from './ConversationPinnedMessage.vue';
import MessageList from 'next/message/MessageList.vue';
import ConversationLabelSuggestion from './conversation/LabelSuggestion.vue';
import Banner from 'dashboard/components/ui/Banner.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ResizableEditorWrapper from './ResizableEditorWrapper.vue';

// stores and apis
import { mapGetters } from 'vuex';

// mixins
import inboxMixin, { INBOX_FEATURES } from 'shared/mixins/inboxMixin';

// utils
import { emitter } from 'shared/helpers/mitt';
import { getTypingUsersText } from '../../../helper/commons';
import { calculateScrollTop } from './helpers/scrollTopCalculationHelper';
import { LocalStorage } from 'shared/helpers/localStorage';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import {
  filterDuplicateSourceMessages,
  getReadMessages,
  getUnreadMessages,
} from 'dashboard/helper/conversationHelper';
import {
  viewerOwnsPersonalInboxUnreadState,
  viewerOwnsUnreadState,
} from 'dashboard/helper/unreadOwnership';

// constants
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { REPLY_POLICY } from 'shared/constants/links';
import wootConstants from 'dashboard/constants/globals';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { isTrainingConversation } from 'dashboard/helper/trainingConversation';
import {
  isAceleresPersonalConversation,
  isAceleresConversationStarted,
} from 'dashboard/helper/aceleresConversationState';
import { sortMessagesChronologically } from 'dashboard/helper/messageSorting';

export default {
  components: {
    MessageList,
    ReplyBox,
    ConversationPinnedMessage,
    Banner,
    ConversationLabelSuggestion,
    Spinner,
    ResizableEditorWrapper,
    ForwardMessageModal,
  },
  mixins: [inboxMixin],
  setup() {
    const {
      isSelectionMode,
      selectedMessages,
      selectedCount,
      hasSelection,
      exitSelectionMode,
      enterSelectionMode,
    } = useMessageSelection();

    const conversationPanelRef = ref(null);
    const resizableEditorWrapperRef = ref(null);
    const messagesViewRef = useTemplateRef('messagesViewRef');
    const topBannerRef = useTemplateRef('topBannerRef');
    const { height: containerHeight } = useElementSize(messagesViewRef);
    const { height: topBannerHeight } = useElementSize(topBannerRef);

    const {
      captainTasksEnabled,
      isLabelSuggestionFeatureEnabled,
      getLabelSuggestions,
    } = useLabelSuggestions();

    provide('contextMenuElementTarget', conversationPanelRef);

    return {
      captainTasksEnabled,
      getLabelSuggestions,
      isLabelSuggestionFeatureEnabled,
      conversationPanelRef,
      resizableEditorWrapperRef,
      messagesViewRef,
      topBannerRef,
      containerHeight,
      topBannerHeight,
      isSelectionMode,
      selectedMessages,
      selectedCount,
      hasSelection,
      exitSelectionMode,
      enterSelectionMode,
    };
  },
  data() {
    return {
      isLoadingPrevious: true,
      heightBeforeLoad: null,
      conversationPanel: null,
      hasUserScrolled: false,
      isProgrammaticScroll: false,
      messageSentSinceOpened: false,
      labelSuggestions: [],
      showForwardModal: false,
      showSelectionMoreMenu: false,
      showDeleteSelectionModal: false,
      deleteSelectionSubmitting: false,
      loadingTimedOut: false,
      spinnerTimer: null,
      stickRaf: null,
      stickAfterSendRaf: null,
      showJumpToBottom: false,
      markingReadConversationId: null,
      simulationReplyClock: Date.now(),
      simulationReplyTimerId: null,
    };
  },

  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
      currentUserId: 'getCurrentUserID',
      listLoadingStatus: 'getAllMessagesLoaded',
      currentAccountId: 'getCurrentAccountId',
    }),
    isOpen() {
      return this.currentChat?.status === wootConstants.STATUS_TYPE.OPEN;
    },
    selectionExitLabel() {
      return 'Sair da seleção';
    },
    selectionForwardLabel() {
      return 'Reenviar mensagens selecionadas';
    },
    selectionLabels() {
      return {
        toolbar: 'Ações das mensagens selecionadas',
        reply: 'Responder à mensagem',
        info: 'Informações da mensagem',
        delete: 'Excluir mensagens selecionadas',
        more: 'Mais ações',
        copy: 'Copiar texto',
        closeMore: 'Fechar mais ações',
      };
    },
    selectedSingleMessage() {
      return this.selectedCount === 1 ? this.selectedMessages[0] : null;
    },
    canReplySelected() {
      const message = this.selectedSingleMessage;
      return Boolean(
        message &&
          !message.private &&
          !['failed', 'progress'].includes(message.status) &&
          this.inboxSupportsReplyTo.outgoing
      );
    },
    canShowSelectedInfo() {
      const message = this.selectedSingleMessage;
      const attachmentType = message?.attachments?.[0]?.fileType;
      return Boolean(
        message &&
          !['failed', 'progress'].includes(message.status) &&
          attachmentType !== 'audio'
      );
    },
    hasSelectedText() {
      return this.selectedMessages.some(message => Boolean(message.content));
    },
    deleteSelectionDescription() {
      const count = this.selectedCount;
      return count === 1
        ? 'Esta mensagem será excluída permanentemente.'
        : `${count} mensagens serão excluídas permanentemente.`;
    },
    shouldShowLabelSuggestions() {
      return (
        this.isOpen &&
        this.captainTasksEnabled &&
        this.isLabelSuggestionFeatureEnabled &&
        !this.messageSentSinceOpened
      );
    },
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    // Pessoais mostram somente a janela atual. O carregador de atendimentos
    // anteriores não deve aparecer nem ser acionado nessa aba.
    isAceleresPersonalConversation() {
      return isAceleresPersonalConversation({
        conversation: this.currentChat,
        inbox: this.inbox,
      });
    },
    // Aceleres: quando a API Oficial está travada, o painel de rodapé
    // (SessionExpiredPanel) cuida do aviso + das 3 ações. Suprime o banner
    // de topo nativo para evitar a mensagem "janela de undefined horas" e a
    // redundância com o painel.
    isAceleresOfficialApiExpired() {
      if (this.currentChat?.can_reply !== false) return false;
      const additionalAttributes = this.inbox?.additional_attributes || {};
      const markedOfficial =
        additionalAttributes.aceleres_official_api === true ||
        additionalAttributes.aceleres_official_api === 'true';
      const webhookUrl = this.inbox?.webhook_url || '';
      const usesAceleresWhatsappWebhook =
        webhookUrl.includes('whatsapp.aceleres.ai') &&
        webhookUrl.includes('/chatwoot/webhook/whatsapp_');

      return (
        this.isAWhatsAppChannel ||
        (this.isAPIInbox && (markedOfficial || usesAceleresWhatsappWebhook))
      );
    },
    typingUsersList() {
      const userList = this.$store.getters[
        'conversationTypingStatus/getUserList'
      ](this.currentChat.id);
      return userList;
    },
    isDailyTrainingConversation() {
      const attributes = this.currentUser?.custom_attributes || {};
      return (
        Boolean(attributes.daily_training_active) &&
        Number(attributes.daily_training_conversation_id || 0) ===
          Number(this.currentChat?.id || 0)
      );
    },
    isTrainingConversationContext() {
      return isTrainingConversation(this.currentChat, this.currentUser);
    },
    isCompletedTrainingConversation() {
      return (
        this.isTrainingConversationContext &&
        this.currentChat?.status === wootConstants.STATUS_TYPE.RESOLVED
      );
    },
    simulationReplyState() {
      if (!this.isDailyTrainingConversation) {
        return null;
      }

      const publicMessages = this.getMessages.filter(message => {
        const messageType = message?.message_type;
        const isActivity = messageType === 2 || messageType === 'activity';
        return !message?.private && !isActivity;
      });
      const lastMessage = publicMessages[publicMessages.length - 1];
      const lastMessageType = lastMessage?.message_type;
      const isOutgoing =
        lastMessageType === 1 || lastMessageType === 'outgoing';
      if (!isOutgoing) {
        return null;
      }

      const rawCreatedAt = lastMessage?.created_at;
      let createdAt = Number(rawCreatedAt);
      if (Number.isFinite(createdAt) && createdAt > 0) {
        createdAt = createdAt > 1000000000000 ? createdAt : createdAt * 1000;
      } else {
        createdAt = Date.parse(rawCreatedAt);
      }

      if (!Number.isFinite(createdAt)) {
        return 'timed_out';
      }

      // Três estados: digitando (0-30s), continua digitando (30-90s) e
      // sem resposta (>90s). O indicador não bloqueia o atendimento.
      const elapsed = this.simulationReplyClock - createdAt;
      if (elapsed <= 30000) {
        return 'pending';
      }
      if (elapsed <= 90000) {
        return 'delayed';
      }
      return 'timed_out';
    },
    isSimulationReplyPending() {
      return (
        this.simulationReplyState === 'pending' ||
        this.simulationReplyState === 'delayed'
      );
    },
    isSimulationReplyTimedOut() {
      return this.simulationReplyState === 'timed_out';
    },
    isAnyoneTyping() {
      const userList = this.typingUsersList;
      return userList.length !== 0 || this.isSimulationReplyPending;
    },
    typingUserNames() {
      if (this.simulationReplyState === 'delayed') {
        return 'O cliente continua digitando';
      }
      if (this.isSimulationReplyPending) {
        return 'Cliente simulado está digitando';
      }
      const userList = this.typingUsersList;
      if (this.isAnyoneTyping) {
        const [i18nKey, params] = getTypingUsersText(userList);
        return this.$t(i18nKey, params);
      }

      return '';
    },
    getMessages() {
      const messages = this.currentChat.messages || [];
      if (this.isAWhatsAppChannel) {
        return sortMessagesChronologically(
          filterDuplicateSourceMessages(messages)
        );
      }
      return sortMessagesChronologically(messages);
    },
    // Aceleres: assinatura que muda ao trocar de conversa ou quando a ultima
    // mensagem muda. Nao inclui a quantidade: carregar historico no inicio da
    // lista nao pode ser confundido com uma mensagem nova e reancorar no fim.
    lastMessageSignature() {
      const messages = this.getMessages;
      const last = messages[messages.length - 1];
      return `${this.currentChat?.id || 'none'}:${last?.id || 'empty'}`;
    },
    readMessages() {
      return getReadMessages(
        this.getMessages,
        this.currentChat.agent_last_seen_at
      );
    },
    unReadMessages() {
      return getUnreadMessages(
        this.getMessages,
        this.currentChat.agent_last_seen_at
      );
    },
    shouldShowSpinner() {
      const hasVisibleMessages = this.getMessages.length > 0;

      return (
        (!hasVisibleMessages &&
          this.currentChat &&
          this.currentChat.dataFetched === undefined) ||
        (!hasVisibleMessages &&
          !this.listLoadingStatus &&
          this.isLoadingPrevious)
      );
    },
    // Check there is a instagram inbox exists with the same instagram_id
    hasDuplicateInstagramInbox() {
      const instagramId = this.inbox.instagram_id;
      const { additional_attributes: additionalAttributes = {} } = this.inbox;
      const instagramInbox =
        this.$store.getters['inboxes/getInstagramInboxByInstagramId'](
          instagramId
        );

      return (
        this.inbox.channel_type === INBOX_TYPES.FB &&
        additionalAttributes.type === 'instagram_direct_message' &&
        instagramInbox
      );
    },

    replyWindowBannerMessage() {
      if (this.isAWhatsAppChannel) {
        return this.$t('CONVERSATION.TWILIO_WHATSAPP_CAN_REPLY');
      }
      if (this.isAPIInbox) {
        const { additional_attributes: additionalAttributes = {} } = this.inbox;
        if (additionalAttributes) {
          const {
            agent_reply_time_window_message: agentReplyTimeWindowMessage,
            agent_reply_time_window: agentReplyTimeWindow,
          } = additionalAttributes;
          return (
            agentReplyTimeWindowMessage ||
            this.$t('CONVERSATION.API_HOURS_WINDOW', {
              hours: agentReplyTimeWindow,
            })
          );
        }
        return '';
      }
      return this.$t('CONVERSATION.CANNOT_REPLY');
    },
    replyWindowLink() {
      if (this.isAFacebookInbox || this.isAnInstagramChannel) {
        return REPLY_POLICY.FACEBOOK;
      }
      if (this.isAWhatsAppCloudChannel) {
        return REPLY_POLICY.WHATSAPP_CLOUD;
      }
      if (this.isATiktokChannel) {
        return REPLY_POLICY.TIKTOK;
      }
      if (!this.isAPIInbox) {
        return REPLY_POLICY.TWILIO_WHATSAPP;
      }
      return '';
    },
    replyWindowLinkText() {
      if (
        this.isAWhatsAppChannel ||
        this.isAFacebookInbox ||
        this.isAnInstagramChannel
      ) {
        return this.$t('CONVERSATION.24_HOURS_WINDOW');
      }
      if (this.isATiktokChannel) {
        return this.$t('CONVERSATION.48_HOURS_WINDOW');
      }
      if (!this.isAPIInbox) {
        return this.$t('CONVERSATION.TWILIO_WHATSAPP_24_HOURS_WINDOW');
      }
      return '';
    },
    unreadMessageCount() {
      // Supervisores e gestores podem visualizar a pendência sem consumir a
      // leitura do responsável. `makeMessagesRead` mantém a autorização de
      // leitura separada da exibição deste contador.
      return this.currentChat.unread_count || 0;
    },
    unreadMessageLabel() {
      const count = this.unreadMessageCount;
      const label =
        this.unreadMessageCount > 1
          ? 'CONVERSATION.UNREAD_MESSAGES'
          : 'CONVERSATION.UNREAD_MESSAGE';
      return `${count} ${this.$t(label)}`;
    },
    viewerOwnsUnreadState() {
      const inboxAttributes = this.inbox?.additional_attributes || {};
      return viewerOwnsUnreadState({
        currentUserId: this.currentUserId,
        assigneeId: this.currentChat?.meta?.assignee?.id,
        isConversationStarted: isAceleresConversationStarted(this.currentChat),
        personalInboxAttributes: {
          ...inboxAttributes,
          connection_type:
            inboxAttributes.connection_type ||
            this.currentChat?.inbox_connection_type,
          owner_chatwoot_user_id:
            inboxAttributes.owner_chatwoot_user_id ||
            this.currentChat?.inbox_owner_chatwoot_user_id,
        },
        personalConversationOwnerId:
          this.currentChat?.additional_attributes
            ?.personal_owner_chatwoot_user_id,
      });
    },
    viewerOwnsPersonalInboxUnreadState() {
      const inboxAttributes = this.inbox?.additional_attributes || {};
      return viewerOwnsPersonalInboxUnreadState({
        currentUserId: this.currentUserId,
        personalInboxAttributes: {
          ...inboxAttributes,
          connection_type:
            inboxAttributes.connection_type ||
            this.currentChat?.inbox_connection_type,
          owner_chatwoot_user_id:
            inboxAttributes.owner_chatwoot_user_id ||
            this.currentChat?.inbox_owner_chatwoot_user_id,
        },
        personalConversationOwnerId:
          this.currentChat?.additional_attributes
            ?.personal_owner_chatwoot_user_id,
      });
    },
    firstUnreadMessageId() {
      if (!this.viewerOwnsUnreadState) {
        return null;
      }

      return this.unReadMessages[0]?.id || null;
    },
    // Aceleres: dados da mensagem fixada para o banner no topo
    pinnedMessageData() {
      const attrs = this.currentChat?.additional_attributes || {};
      if (!attrs.pinned_message_id) return null;
      return {
        id: attrs.pinned_message_id,
        content: attrs.pinned_message?.content || '',
      };
    },
    conversationHistoryMeta() {
      const liveMeta = this.$store.getters[
        'conversationMetadata/getConversationMetadata'
      ](this.currentChat.id);
      return liveMeta?.history || this.currentChat?.meta?.history || {};
    },
    canLoadPreviousMessages() {
      return (
        !this.isAceleresPersonalConversation &&
        this.conversationHistoryMeta?.can_load_previous === true
      );
    },
    shouldShowPreviousMessagesButton() {
      if (!this.currentChat?.id || this.isAceleresPersonalConversation) {
        return false;
      }

      if (this.isLoadingPrevious || this.canLoadPreviousMessages) {
        return true;
      }

      const historyFlag = this.conversationHistoryMeta?.can_load_previous;
      if (historyFlag === false || this.listLoadingStatus) {
        return false;
      }

      return (this.currentChat.messages?.length || 0) > 0;
    },
    inboxSupportsReplyTo() {
      const incoming = this.inboxHasFeature(INBOX_FEATURES.REPLY_TO);
      const outgoing =
        this.inboxHasFeature(INBOX_FEATURES.REPLY_TO_OUTGOING) &&
        !this.is360DialogWhatsAppChannel;

      return { incoming, outgoing };
    },
  },

  watch: {
    currentChat(newChat, oldChat) {
      if (newChat.id === oldChat.id) {
        return;
      }
      this.handleExitSelection();
      // Aceleres: nova conversa sempre comeca ancorada no fim (descarta o
      // scroll manual da conversa anterior) para abrir nas mensagens recentes.
      this.hasUserScrolled = false;
      this.fetchAllAttachmentsFromCurrentChat();
      this.fetchSuggestions();
      this.messageSentSinceOpened = false;
      this.resetReplyEditorHeight();
      clearTimeout(this.spinnerTimer);
      this.spinnerTimer = null;
      this.loadingTimedOut = false;
      this.anchorToBottomOnOpen();
      this.$nextTick(() => this.markCurrentConversationReadIfNeeded());
    },
    isSelectionMode(active) {
      if (active) return;
      this.showSelectionMoreMenu = false;
      this.showDeleteSelectionModal = false;
      this.deleteSelectionSubmitting = false;
    },
    unreadMessageCount(newCount, oldCount) {
      if (newCount > 0 && newCount !== oldCount) {
        this.$nextTick(() => this.markCurrentConversationReadIfNeeded());
      }
    },
    // Aceleres: reancora somente ao trocar de conversa ou receber uma nova
    // mensagem no fim. Mensagens antigas inseridas no inicio preservam o scroll.
    lastMessageSignature() {
      this.$nextTick(() => this.scrollToConversationBottomIfNeeded());
    },
  },

  created() {
    emitter.on(BUS_EVENTS.SCROLL_TO_MESSAGE, this.onScrollToMessage);
    // when a message is sent we set the flag to true this hides the label suggestions,
    // until the chat is changed and the flag is reset in the watch for currentChat
    emitter.on(BUS_EVENTS.MESSAGE_SENT, this.onMessageSent);
  },

  mounted() {
    this.addScrollListener();
    this.fetchAllAttachmentsFromCurrentChat();
    this.fetchSuggestions();
    this.$nextTick(() => this.markCurrentConversationReadIfNeeded());
    this.simulationReplyTimerId = window.setInterval(() => {
      this.simulationReplyClock = Date.now();
    }, 1000);
  },

  unmounted() {
    this.handleExitSelection();
    if (this.simulationReplyTimerId) {
      window.clearInterval(this.simulationReplyTimerId);
      this.simulationReplyTimerId = null;
    }
    this.removeBusListeners();
    this.removeScrollListener();
  },

  methods: {
    // Aceleres: desafixar mensagem via banner
    async handleUnpin() {
      const attrs = this.currentChat?.additional_attributes || {};
      const msgId = attrs.pinned_message_id;
      if (!msgId) return;
      await this.$store.dispatch('pinMessage', {
        accountId: this.currentAccountId,
        conversationId: this.currentChat.id,
        messageId: msgId,
      });
    },
    // Aceleres: scroll ate a mensagem fixada
    scrollToPinnedMessage(messageId) {
      const el = document.getElementById('message' + messageId);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
    async fetchSuggestions() {
      // start empty, this ensures that the label suggestions are not shown
      this.labelSuggestions = [];

      if (this.isLabelSuggestionDismissed()) {
        return;
      }

      // Early exit if conversation already has labels - no need to suggest more
      const existingLabels = this.currentChat?.labels || [];
      if (existingLabels.length > 0) return;

      if (!this.captainTasksEnabled || !this.isLabelSuggestionFeatureEnabled) {
        return;
      }

      this.labelSuggestions = await this.getLabelSuggestions();

      // once the labels are fetched, we need to scroll to bottom
      // but we need to wait for the DOM to be updated
      // so we use the nextTick method
      this.$nextTick(() => {
        // this param is added to route, telling the UI to navigate to the message
        // it is triggered by the SCROLL_TO_MESSAGE method
        // see setActiveChat on ConversationView.vue for more info
        const { messageId } = this.$route.query;

        // only trigger the scroll to bottom if the user has not scrolled
        // and there's no active messageId that is selected in view
        if (!messageId && !this.hasUserScrolled) {
          this.scrollToBottom();
        }
      });
    },
    isLabelSuggestionDismissed() {
      return LocalStorage.getFlag(
        LOCAL_STORAGE_KEYS.DISMISSED_LABEL_SUGGESTIONS,
        this.currentAccountId,
        this.currentChat.id
      );
    },
    fetchAllAttachmentsFromCurrentChat() {
      this.$store.dispatch('fetchAllAttachments', this.currentChat.id);
    },
    removeBusListeners() {
      emitter.off(BUS_EVENTS.SCROLL_TO_MESSAGE, this.onScrollToMessage);
      emitter.off(BUS_EVENTS.MESSAGE_SENT, this.onMessageSent);
      if (this.stickAfterSendRaf) {
        window.cancelAnimationFrame(this.stickAfterSendRaf);
        this.stickAfterSendRaf = null;
      }
    },
    // Aceleres: ao enviar msg/imagem, força o scroll a ficar colado no fim
    // por ~1.5s. Um simples scrollToBottom não bastava pra imagens: o <img>
    // carrega depois, cresce o painel e a msg recém-enviada saía da viewport.
    // Também zera hasUserScrolled — quem acabou de enviar quer ver o próprio
    // envio, mesmo que tenha rolado pra cima antes. (pedido 2026-07-01)
    onMessageSent() {
      this.messageSentSinceOpened = true;
      this.stickToBottomAfterSend();
    },
    stickToBottomAfterSend() {
      this.hasUserScrolled = false;
      if (this.stickAfterSendRaf) {
        window.cancelAnimationFrame(this.stickAfterSendRaf);
        this.stickAfterSendRaf = null;
      }
      this.$nextTick(() => {
        this.scrollToBottom();
        let lastHeight = -1;
        let lastClientHeight = -1;
        let frames = 0;
        const step = () => {
          const panel = this.ensurePanel();
          if (!panel) {
            frames += 1;
            if (frames < 90) {
              this.stickAfterSendRaf = window.requestAnimationFrame(step);
              return;
            }
            this.stickAfterSendRaf = null;
            this.refreshJumpToBottom();
            return;
          }
          if (this.hasUserScrolled) {
            this.stickAfterSendRaf = null;
            this.refreshJumpToBottom();
            return;
          }
          if (
            panel.scrollHeight !== lastHeight ||
            panel.clientHeight !== lastClientHeight
          ) {
            lastHeight = panel.scrollHeight;
            lastClientHeight = panel.clientHeight;
            this.isProgrammaticScroll = true;
            panel.scrollTop = panel.scrollHeight;
          }
          frames += 1;
          if (frames < 90) {
            this.stickAfterSendRaf = window.requestAnimationFrame(step);
          } else {
            this.stickAfterSendRaf = null;
            this.refreshJumpToBottom();
          }
        };
        step();
      });
    },
    onScrollToMessage({ messageId = '' } = {}) {
      this.$nextTick(() => {
        const messageElement = document.getElementById('message' + messageId);
        if (messageElement) {
          this.isProgrammaticScroll = true;
          // Aceleres: block:'center' deixa o destino bem no meio da viewport
          // (em vez de colado no topo, que tava deixando a msg quase fora);
          // e o highlight class faz um flash de fundo pra agente saber pra
          // onde foi (mimic do WhatsApp). Estilo da classe está em
          // aceleres-overrides.css.
          messageElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
          messageElement.classList.add('acl-scroll-highlight');
          setTimeout(() => {
            messageElement.classList.remove('acl-scroll-highlight');
          }, 1400);
        } else {
          this.scrollToBottom();
        }
      });
      this.makeMessagesRead();
    },
    addScrollListener(attempt = 0) {
      // Aceleres: o painel (.conversation-panel) pode ainda nao existir no
      // primeiro mounted (MessageList renderiza depois). Resolve via ensurePanel
      // e, se nao achar, tenta de novo no proximo frame (ate ~30) antes de
      // anexar listeners + ancorar. Sem isso, conversationPanel ficava null, o
      // scroll quebrava (TypeError scrollHeight) e o botao nunca aparecia.
      this.isLoadingPrevious = false;
      const panel = this.ensurePanel();
      if (!panel) {
        if (attempt < 30) {
          window.requestAnimationFrame(() => this.addScrollListener(attempt + 1));
        }
        return;
      }
      this.setScrollParams();
      panel.removeEventListener('scroll', this.handleScroll);
      panel.addEventListener('scroll', this.handleScroll);
      panel.removeEventListener('load', this.onMediaLoad, true);
      panel.addEventListener('load', this.onMediaLoad, true);
      this.anchorToBottomOnOpen();
    },
    removeScrollListener() {
      if (this.stickRaf) {
        window.cancelAnimationFrame(this.stickRaf);
        this.stickRaf = null;
      }
      if (!this.conversationPanel) return;
      this.conversationPanel.removeEventListener('scroll', this.handleScroll);
      this.conversationPanel.removeEventListener('load', this.onMediaLoad, true);
    },
    // Aceleres: resolve o elemento rolavel (.conversation-panel) de forma
    // resiliente -- cache (se ainda conectado), senao o $el do ref do
    // MessageList, senao querySelector no $el. Evita o null que quebrava
    // scrollToBottom e escondia o botao quando o painel nao estava pronto.
    ensurePanel() {
      if (this.conversationPanel && this.conversationPanel.isConnected) {
        return this.conversationPanel;
      }
      const exposedElement = this.conversationPanelRef?.messageListElement;
      const fromExpose = exposedElement?.value || exposedElement;
      const fromRef = this.conversationPanelRef && this.conversationPanelRef.$el;
      this.conversationPanel =
        (fromExpose && fromExpose.isConnected ? fromExpose : null) ||
        (fromRef && fromRef.classList && fromRef.classList.contains('conversation-panel')
          ? fromRef
          : null) ||
        (this.$el && this.$el.querySelector
          ? this.$el.querySelector('.conversation-panel')
          : null) ||
        null;
      return this.conversationPanel;
    },
    scrollToBottom() {
      const panel = this.ensurePanel();
      if (!panel) return;
      this.isProgrammaticScroll = true;
      // Aceleres: sempre rola até o fim absoluto (mensagens mais recentes),
      // independente de mensagens não-lidas — comportamento esperado de chat.
      // Antes, com muitas mensagens não-lidas, o painel abria no topo.
      panel.scrollTop = panel.scrollHeight;
      this.showJumpToBottom = false;
    },
    // Aceleres: visibilidade do botao flutuante "ir para o fim" pela distancia
    // ate o fim -- independe da heuristica de auto-scroll (que pode falhar).
    refreshJumpToBottom() {
      const panel = this.ensurePanel();
      if (!panel) {
        this.showJumpToBottom = false;
        return;
      }
      const d = panel.scrollHeight - panel.scrollTop - panel.clientHeight;
      this.showJumpToBottom = d > 200;
    },
    // Aceleres: clique do botao -> desce de vez e some.
    jumpToBottom() {
      this.hasUserScrolled = false;
      this.scrollToBottom();
    },
    // Aceleres: ao carregar uma mídia (ex.: imagem), a altura do painel muda e
    // empurra o conteúdo. Se o usuário não rolou manualmente, reancora no fim
    // para manter as mensagens mais recentes visíveis.
    onMediaLoad() {
      if (!this.hasUserScrolled && this.conversationPanel) {
        this.$nextTick(() => this.scrollToBottom());
      } else {
        this.$nextTick(() => this.refreshJumpToBottom());
      }
    },
    // Aceleres: rola pro fim da conversa quando faz sentido -- nao interfere se
    // o usuario rolou pra cima, ha deep-link ou ha mensagens nao-lidas pendentes.
    scrollToConversationBottomIfNeeded() {
      if (!this.ensurePanel()) return;
      const { messageId } = this.$route.query;
      if (messageId || this.hasUserScrolled) {
        this.$nextTick(() => this.refreshJumpToBottom());
        return;
      }
      this.scrollToBottom();
      // 2o passe no proximo frame: pega reflows tardios (markdown, fontes, cards
      // que so calculam altura depois do paint) sem depender de carga de midia.
      window.requestAnimationFrame(() => {
        if (!this.hasUserScrolled && this.conversationPanel) {
          this.scrollToBottom();
        }
        this.refreshJumpToBottom();
      });
    },
    // Aceleres: ao ABRIR/TROCAR de conversa, "gruda" no fim por ~1.5s. Enquanto
    // o conteudo ainda muda de altura (mensagens do fetch async, imagens, reflow
    // de markdown), reancora no fim a cada mudanca de scrollHeight. Para assim
    // que o usuario rola manualmente (hasUserScrolled) ou apos ~90 frames.
    anchorToBottomOnOpen() {
      if (this.stickRaf) {
        window.cancelAnimationFrame(this.stickRaf);
        this.stickRaf = null;
      }
      let lastHeight = -1;
      let frames = 0;
      const step = () => {
        const panel = this.ensurePanel();
        if (this.hasUserScrolled || !panel) {
          this.stickRaf = null;
          this.refreshJumpToBottom();
          return;
        }

        const height = panel.scrollHeight;
        if (height !== lastHeight) {
          lastHeight = height;
          this.scrollToBottom();
        }
        frames += 1;
        if (frames < 90) {
          this.stickRaf = window.requestAnimationFrame(step);
        } else {
          this.stickRaf = null;
          this.refreshJumpToBottom();
        }
      };
      this.stickRaf = window.requestAnimationFrame(step);
    },
    setScrollParams() {
      const panel = this.ensurePanel();
      if (!panel) return;
      this.heightBeforeLoad = panel.scrollHeight;
      this.scrollTopBeforeLoad = panel.scrollTop;
    },

    async loadMoreMessages() {
      if (this.listLoadingStatus || this.isLoadingPrevious) return;
      this.isLoadingPrevious = true;
      try {
        await this.$store.dispatch('fetchPreviousMessages', {
          conversationId: this.currentChat.id,
          before: this.currentChat.messages[0].id,
        });
      } catch {
        // ignore
      } finally {
        this.isLoadingPrevious = false;
      }
    },
    async fetchPreviousMessages(scrollTop = 0) {
      this.setScrollParams();
      const shouldLoadMoreMessages =
        this.currentChat.dataFetched === true &&
        !this.listLoadingStatus &&
        !this.isLoadingPrevious;

      if (
        scrollTop < 100 &&
        !this.isLoadingPrevious &&
        shouldLoadMoreMessages
      ) {
        this.isLoadingPrevious = true;
        try {
          await this.$store.dispatch('fetchPreviousMessages', {
            conversationId: this.currentChat.id,
            before: this.currentChat.messages[0].id,
          });
          const heightDifference =
            this.conversationPanel.scrollHeight - this.heightBeforeLoad;
          this.conversationPanel.scrollTop =
            this.scrollTopBeforeLoad + heightDifference;
          this.setScrollParams();
        } catch (error) {
          // Ignore Error
        } finally {
          this.isLoadingPrevious = false;
        }
      }
    },

    handleScroll(e) {
      // Aceleres: detecta scroll do usuário pela DISTÂNCIA até o fim, não por
      // flag de uso único (isProgrammaticScroll), que sofria race com os
      // scrolls programáticos do anchorToBottomOnOpen e marcava
      // hasUserScrolled=true por engano -> a conversa abria no topo. Perto do
      // fim => não rolou; longe => rolou (lê histórico ou carregou antigas).
      const panel = this.conversationPanel || e.target;
      const distanceFromBottom =
        panel.scrollHeight - panel.scrollTop - panel.clientHeight;
      this.hasUserScrolled = distanceFromBottom > 80;
      this.showJumpToBottom = distanceFromBottom > 200;
      emitter.emit(BUS_EVENTS.ON_MESSAGE_LIST_SCROLL);
    },
    // Quando o usuário rola pro topo pra carregar mensagens antigas, inicia um
    // timer único de 10s. Se nesse tempo nenhuma mensagem nova for carregada,
    // mostra o aviso pra consultar pelo celular. Não reinicia em cada scroll.
    maybeStartLoadingTimer() {
      if (this.spinnerTimer || this.loadingTimedOut) return;
      if (this.listLoadingStatus) return; // todas as mensagens já carregadas
      const before = this.currentChat?.messages?.length || 0;
      this.spinnerTimer = setTimeout(() => {
        this.spinnerTimer = null;
        const after = this.currentChat?.messages?.length || 0;
        if (after <= before && !this.listLoadingStatus) {
          this.loadingTimedOut = true;
        }
      }, 10000);
    },

    makeMessagesRead() {
      // Aceleres: só marca como lida se o usuário atual é o dono do estado de
      // leitura da conversa. Abrir conversa de outro agente não zera o não-lido.
      if (
        !this.viewerOwnsUnreadState &&
        !this.viewerOwnsPersonalInboxUnreadState
      ) {
        return;
      }
      const conversationId = this.currentChat?.id;
      if (
        !conversationId ||
        this.markingReadConversationId === conversationId
      ) {
        return;
      }

      this.markingReadConversationId = conversationId;
      this.$store
        .dispatch('markMessagesRead', { id: conversationId })
        .finally(() => {
          if (this.markingReadConversationId === conversationId) {
            this.markingReadConversationId = null;
          }
        });
    },
    markCurrentConversationReadIfNeeded() {
      if (!this.currentChat?.id || this.unreadMessageCount <= 0) {
        return;
      }

      this.makeMessagesRead();
    },
    async handleMessageRetry(message) {
      if (!message) return;
      const payload = useSnakeCase(message);
      await this.$store.dispatch('sendMessageWithData', payload);
    },
    toggleReplyEditorSize() {
      this.resizableEditorWrapperRef?.toggleEditorExpand?.();
    },
    resetReplyEditorHeight() {
      this.resizableEditorWrapperRef?.resetEditorHeight?.();
    },
    closeSelectionInteractions() {
      emitter.emit(BUS_EVENTS.MESSAGE_INTERACTION_OPENED, {
        messageId: null,
        interaction: null,
      });
    },
    handleExitSelection() {
      this.showSelectionMoreMenu = false;
      this.showDeleteSelectionModal = false;
      this.closeSelectionInteractions();
      this.exitSelectionMode();
    },
    handleReplySelected() {
      const message = this.selectedSingleMessage;
      if (!message || !this.canReplySelected) return;
      LocalStorage.updateJsonStore(
        LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO,
        this.currentChat.id,
        message.id
      );
      this.handleExitSelection();
      this.$nextTick(() => {
        emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, message);
      });
    },
    handleInfoSelected() {
      const message = this.selectedSingleMessage;
      if (!message || !this.canShowSelectedInfo) return;
      this.handleExitSelection();
      this.$nextTick(() => {
        emitter.emit(BUS_EVENTS.OPEN_MESSAGE_INFO, { messageId: message.id });
      });
    },
    openSelectedDeleteModal() {
      if (!this.hasSelection) return;
      this.showSelectionMoreMenu = false;
      this.showDeleteSelectionModal = true;
    },
    closeSelectedDeleteModal() {
      if (this.deleteSelectionSubmitting) return;
      this.showDeleteSelectionModal = false;
    },
    async handleDeleteSelected() {
      if (!this.hasSelection || this.deleteSelectionSubmitting) return;
      this.deleteSelectionSubmitting = true;
      const conversationId = this.currentChat.id;
      const deleteActions = this.selectedMessages.map(m =>
        this.$store.dispatch('deleteMessage', {
          conversationId,
          messageId: m.id,
        })
      );
      const results = await Promise.allSettled(deleteActions);
      const failures = results.filter(result => result.status === 'rejected');
      this.deleteSelectionSubmitting = false;
      this.showDeleteSelectionModal = false;

      if (failures.length) {
        useAlert('Não foi possível excluir todas as mensagens selecionadas.');
      } else {
        useAlert(this.$t('CONVERSATION.SUCCESS_DELETE_MESSAGE'));
      }
      this.handleExitSelection();
    },
    openSelectedForwardModal() {
      if (!this.hasSelection) return;
      this.showSelectionMoreMenu = false;
      this.closeSelectionInteractions();
      this.showForwardModal = true;
    },
    toggleSelectionMoreMenu() {
      this.showSelectionMoreMenu = !this.showSelectionMoreMenu;
    },
    closeSelectionMoreMenu() {
      this.showSelectionMoreMenu = false;
    },
    async copySelectedText() {
      const text = this.selectedMessages
        .map(message => message.content)
        .filter(Boolean)
        .join('\n\n');
      if (!text) return;
      try {
        await copyTextToClipboard(text);
        useAlert('Mensagens copiadas.');
        this.handleExitSelection();
      } catch (error) {
        useAlert('Não foi possível copiar as mensagens.');
      }
    },
  },
};
</script>

<template>
  <div
    ref="messagesViewRef"
    class="acl-messages-view acl-conversation-mobile-messages flex min-h-0 min-w-0 flex-1 flex-col justify-between m-0 overflow-hidden scrollbar-thin"
  >
    <div ref="topBannerRef">
      <Banner
        v-if="hasDuplicateInstagramInbox"
        color-scheme="alert"
        class="mx-2 mt-2 overflow-hidden rounded-lg"
        :banner-message="$t('CONVERSATION.OLD_INSTAGRAM_INBOX_REPLY_BANNER')"
      />
    </div>
    <div
      v-if="shouldShowPreviousMessagesButton"
      class="flex justify-center py-2 flex-shrink-0"
    >
      <button
        class="acl-load-previous-button flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold border transition-colors"
        :class="isLoadingPrevious
          ? 'text-n-slate-10 border-n-slate-5 bg-n-surface-2 cursor-wait'
          : 'text-n-brand border-n-brand/40 bg-n-brand/5 hover:bg-n-brand/15 cursor-pointer'"
        :disabled="isLoadingPrevious"
        title="Ver atendimentos anteriores"
        @click="loadMoreMessages()"
      >
        <svg v-if="!isLoadingPrevious" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="animate-spin"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
        {{ isLoadingPrevious ? 'Carregando...' : 'Ver atendimentos anteriores' }}
      </button>
    </div>
    <ConversationPinnedMessage
      :pinned-message="pinnedMessageData"
      @unpin="handleUnpin"
      @scroll-to="scrollToPinnedMessage"
    />
    <MessageList
      ref="conversationPanelRef"
      class="acl-message-list conversation-panel relative z-0 flex min-h-0 flex-1 m-0 flex-col overflow-y-auto pb-4"
      :style="
        isAnyoneTyping || isSimulationReplyTimedOut
          ? { paddingBottom: '7rem' }
          : null
      "
      :current-user-id="currentUserId"
      :first-unread-id="firstUnreadMessageId"
      :is-an-email-channel="isAnEmailChannel"
      :inbox-supports-reply-to="inboxSupportsReplyTo"
      :messages="getMessages"
      @retry="handleMessageRetry"
    >
      <template #beforeAll>
        <transition name="slide-up">
          <!-- eslint-disable-next-line vue/require-toggle-inside-transition -->
          <li
            class="min-h-[4rem] flex flex-shrink-0 flex-grow-0 items-center flex-auto justify-center max-w-full mt-0 mr-0 mb-1 ml-0 relative first:mt-auto last:mb-0"
          >
            <Spinner
              v-if="shouldShowSpinner && !loadingTimedOut"
              class="text-n-brand"
            />
            <span
              v-else-if="loadingTimedOut"
              class="text-sm text-n-slate-11 text-center px-6 py-2 leading-snug"
            >
              Não foi possível carregar as mensagens anteriores aqui. Para
              visualizá-las, consulte a conversa pelo celular (WhatsApp).
            </span>
          </li>
        </transition>
      </template>
      <template #unreadBadge>
        <li
          v-show="unreadMessageCount != 0"
          class="list-none flex justify-center items-center"
        >
          <span
            class="shadow-lg rounded-full bg-n-brand text-white text-xs font-medium my-2.5 mx-auto px-2.5 py-1.5"
          >
            {{ unreadMessageLabel }}
          </span>
        </li>
      </template>
      <template #after>
        <ConversationLabelSuggestion
          v-if="shouldShowLabelSuggestions"
          :suggested-labels="labelSuggestions"
          :chat-labels="currentChat.labels"
          :conversation-id="currentChat.id"
        />
      </template>
    </MessageList>
    <div
      class="acl-conversation-composer-shell acl-conversation-mobile-composer relative flex flex-none flex-col bg-transparent"
      :style="{ zIndex: 2147482000, isolation: 'isolate' }"
    >
      <div
        v-if="isAnyoneTyping || isSimulationReplyTimedOut"
        class="pointer-events-none absolute inset-x-0 flex justify-center"
        :style="{ top: '-3.5rem', zIndex: 2147483000 }"
      >
        <div
          class="pointer-events-auto mx-auto flex max-w-[calc(100%-2rem)] items-center rounded-full border border-n-weak bg-white py-2 pl-5 pr-4 text-xs font-semibold text-n-slate-11 shadow-md dark:bg-n-solid-3"
          :style="{ zIndex: 2147483000 }"
        >
          <span>
            {{
              isSimulationReplyTimedOut && typingUsersList.length === 0
                ? 'O cliente não respondeu, você pode enviar uma mensagem se achar necessário.'
                : typingUserNames
            }}
          </span>
          <img
            v-if="isAnyoneTyping && !isSimulationReplyTimedOut"
            class="w-6 ltr:ml-2 rtl:mr-2"
            src="assets/images/typing.gif"
            alt="Someone is typing"
          />
        </div>
      </div>
      <transition name="slide-up">
        <button
          v-show="showJumpToBottom"
          type="button"
          class="acl-jump-to-bottom absolute -top-14 ltr:right-4 rtl:left-4 z-20 flex items-center justify-center w-11 h-11 rounded-full bg-white text-n-slate-12 border border-slate-200 shadow-[0_2px_10px_rgba(15,23,42,0.18)] transition-transform hover:scale-105"
          title="Ir para a última mensagem"
          aria-label="Ir para a última mensagem"
          @click="jumpToBottom()"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
        </button>
      </transition>
      <ResizableEditorWrapper
        v-show="!isCompletedTrainingConversation"
        :class="{ 'md:hidden': isSelectionMode }"
        ref="resizableEditorWrapperRef"
        :container-height="Math.max(0, containerHeight - topBannerHeight)"
      >
        <ReplyBox @toggle-editor-size="toggleReplyEditorSize" />
      </ResizableEditorWrapper>
      <div
        v-if="isCompletedTrainingConversation && !isSelectionMode"
        class="mx-4 mb-3 rounded-lg border border-n-weak bg-n-solid-2 px-4 py-3 text-center text-sm font-medium text-n-slate-11"
      >
        Treino concluído. Novas mensagens ficam bloqueadas nesta conversa.
      </div>

      <!-- Selection action bar -->
      <transition name="slide-up">
        <div
          v-if="isSelectionMode"
          class="acl-message-selection-footer flex min-h-16 items-center justify-between border-t border-n-weak bg-n-background px-4 py-2 shadow-[0_-2px_8px_rgba(15,23,42,0.08)]"
        >
          <div class="flex items-center gap-3">
            <button
              type="button"
              class="flex items-center justify-center w-9 h-9 rounded-full hover:bg-n-alpha-2 transition-colors"
              :aria-label="selectionExitLabel"
              @click="handleExitSelection"
            >
              <span class="i-lucide-x w-5 h-5 text-n-slate-11" />
            </button>
            <span class="text-base font-medium text-n-slate-12">
              {{
                $t('CONVERSATION.FORWARD.SELECTED_COUNT', {
                  count: selectedCount,
                })
              }}
            </span>
          </div>
          <button
            type="button"
            class="flex size-12 items-center justify-center rounded-full text-n-slate-12 transition-colors hover:bg-n-alpha-2 disabled:opacity-40"
            :disabled="!hasSelection"
            :aria-label="selectionForwardLabel"
            @click="openSelectedForwardModal"
          >
            <svg
              viewBox="0 0 24 24"
              width="28"
              height="28"
              preserveAspectRatio="xMidYMid meet"
              fill="none"
              aria-hidden="true"
              style="display:block"
            >
              <path
                fill="currentColor"
                d="M19.18 11 15.3 7.13a.97.97 0 0 1-.3-.72c0-.27.1-.51.3-.71.2-.18.44-.27.71-.27.28 0 .5.09.69.27l4.6 4.6c.1.1.17.2.21.32a1.2 1.2 0 0 1 0 .76.88.88 0 0 1-.21.32l-4.6 4.6a.9.9 0 0 1-.7.29c-.27-.01-.5-.1-.7-.29a1 1 0 0 1-.31-.7.87.87 0 0 1 .29-.7l3.9-3.9Zm-6 1H7c-.83 0-1.54.3-2.13.88A2.9 2.9 0 0 0 4 15v3c0 .28-.1.52-.29.71A.94.94 0 0 1 3 19a.97.97 0 0 1-.71-.29A.97.97 0 0 1 2 18v-3c0-1.38.49-2.56 1.46-3.54A4.82 4.82 0 0 1 7 10h6.18L10.3 7.13a.97.97 0 0 1-.3-.72c0-.27.1-.51.3-.71.2-.18.44-.27.71-.27.28 0 .5.09.69.27l4.6 4.6c.1.1.17.2.21.32a1.2 1.2 0 0 1 0 .76.88.88 0 0 1-.21.32l-4.6 4.6a.9.9 0 0 1-.7.29c-.27-.01-.5-.1-.7-.29a1 1 0 0 1-.31-.7.87.87 0 0 1 .29-.7l2.9-2.9Z"
              />
            </svg>
          </button>
        </div>
      </transition>
    </div>
  </div>

  <Teleport to="body">
    <Transition name="aceleres-mobile-selection-header">
      <header
        v-if="isSelectionMode"
        class="aceleres-mobile-message-selection-header"
      >
        <button
          v-if="showSelectionMoreMenu"
          type="button"
          class="aceleres-mobile-message-selection-backdrop"
          :aria-label="selectionLabels.closeMore"
          @click="closeSelectionMoreMenu"
        />
        <div
          class="aceleres-mobile-message-selection-bar"
          role="toolbar"
          :aria-label="selectionLabels.toolbar"
        >
          <button
            type="button"
            class="aceleres-mobile-message-selection-button"
            :aria-label="selectionExitLabel"
            @click="handleExitSelection"
          >
            <span class="i-lucide-arrow-left size-6" aria-hidden="true" />
          </button>

          <span
            class="aceleres-mobile-message-selection-count"
            aria-live="polite"
          >
            {{ selectedCount }}
          </span>

          <span class="aceleres-mobile-message-selection-spacer" />

          <button
            v-if="canReplySelected"
            type="button"
            class="aceleres-mobile-message-selection-button"
            :aria-label="selectionLabels.reply"
            @click="handleReplySelected"
          >
            <span class="i-lucide-reply size-6" aria-hidden="true" />
          </button>

          <button
            v-if="canShowSelectedInfo"
            type="button"
            class="aceleres-mobile-message-selection-button"
            :aria-label="selectionLabels.info"
            @click="handleInfoSelected"
          >
            <span class="i-lucide-info size-6" aria-hidden="true" />
          </button>

          <button
            type="button"
            class="aceleres-mobile-message-selection-button"
            :disabled="!hasSelection"
            :aria-label="selectionLabels.delete"
            @click="openSelectedDeleteModal"
          >
            <span class="i-lucide-trash-2 size-6" aria-hidden="true" />
          </button>

          <button
            type="button"
            class="aceleres-mobile-message-selection-button"
            :disabled="!hasSelection"
            :aria-label="selectionForwardLabel"
            @click="openSelectedForwardModal"
          >
            <svg
              viewBox="0 0 24 24"
              width="27"
              height="27"
              fill="none"
              aria-hidden="true"
            >
              <path
                fill="currentColor"
                d="M19.18 11 15.3 7.13a.97.97 0 0 1-.3-.72c0-.27.1-.51.3-.71.2-.18.44-.27.71-.27.28 0 .5.09.69.27l4.6 4.6c.1.1.17.2.21.32a1.2 1.2 0 0 1 0 .76.88.88 0 0 1-.21.32l-4.6 4.6a.9.9 0 0 1-.7.29c-.27-.01-.5-.1-.7-.29a1 1 0 0 1-.31-.7.87.87 0 0 1 .29-.7l3.9-3.9Zm-6 1H7c-.83 0-1.54.3-2.13.88A2.9 2.9 0 0 0 4 15v3c0 .28-.1.52-.29.71A.94.94 0 0 1 3 19a.97.97 0 0 1-.71-.29A.97.97 0 0 1 2 18v-3c0-1.38.49-2.56 1.46-3.54A4.82 4.82 0 0 1 7 10h6.18L10.3 7.13a.97.97 0 0 1-.3-.72c0-.27.1-.51.3-.71.2-.18.44-.27.71-.27.28 0 .5.09.69.27l4.6 4.6c.1.1.17.2.21.32a1.2 1.2 0 0 1 0 .76.88.88 0 0 1-.21.32l-4.6 4.6a.9.9 0 0 1-.7.29c-.27-.01-.5-.1-.7-.29a1 1 0 0 1-.31-.7.87.87 0 0 1 .29-.7l2.9-2.9Z"
              />
            </svg>
          </button>

          <button
            type="button"
            class="aceleres-mobile-message-selection-button"
            :aria-label="selectionLabels.more"
            :aria-expanded="showSelectionMoreMenu"
            @click="toggleSelectionMoreMenu"
          >
            <span class="i-lucide-more-vertical size-6" aria-hidden="true" />
          </button>

          <nav
            v-if="showSelectionMoreMenu"
            class="aceleres-mobile-message-selection-menu"
            :aria-label="selectionLabels.more"
          >
            <button
              type="button"
              :disabled="!hasSelectedText"
              @click="copySelectedText"
            >
              <span class="i-lucide-copy size-5" aria-hidden="true" />
              <span>{{ selectionLabels.copy }}</span>
            </button>
            <button type="button" @click="handleExitSelection">
              <span class="i-lucide-x size-5" aria-hidden="true" />
              <span>{{ selectionExitLabel }}</span>
            </button>
          </nav>
        </div>
      </header>
    </Transition>
  </Teleport>

  <Teleport to="body">
    <Transition name="aceleres-selection-delete-modal">
      <div
        v-if="showDeleteSelectionModal"
        class="aceleres-selection-delete-layer"
        role="presentation"
      >
        <button
          type="button"
          class="aceleres-selection-delete-backdrop"
          :aria-label="
            $t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.CANCEL')
          "
          @click="closeSelectedDeleteModal"
        />
        <section
          class="aceleres-selection-delete-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="aceleres-selection-delete-title"
          aria-describedby="aceleres-selection-delete-description"
          @click.stop
        >
          <div class="aceleres-selection-delete-content">
            <span
              class="aceleres-selection-delete-icon i-lucide-trash-2"
              aria-hidden="true"
            />
            <div>
              <h3 id="aceleres-selection-delete-title">
                {{ $t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.TITLE') }}
              </h3>
              <p id="aceleres-selection-delete-description">
                {{ deleteSelectionDescription }}
              </p>
            </div>
          </div>
          <footer class="aceleres-selection-delete-actions">
            <button
              type="button"
              :disabled="deleteSelectionSubmitting"
              @click="closeSelectedDeleteModal"
            >
              {{ $t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.CANCEL') }}
            </button>
            <button
              type="button"
              class="is-danger"
              :disabled="deleteSelectionSubmitting"
              @click="handleDeleteSelected"
            >
              <span
                v-if="deleteSelectionSubmitting"
                class="i-lucide-loader-circle size-4 animate-spin"
                aria-hidden="true"
              />
              {{ $t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.DELETE') }}
            </button>
          </footer>
        </section>
      </div>
    </Transition>
  </Teleport>

  <!-- Forward modal -->
  <ForwardMessageModal
    v-if="showForwardModal"
    v-model:show="showForwardModal"
    :conversation-id="currentChat.id"
    :selected-messages="selectedMessages"
    @success="handleExitSelection"
  />
</template>

<style scoped>
.acl-jump-to-bottom {
  color: rgba(0, 0, 0, 0.6);
}

.aceleres-mobile-message-selection-header {
  display: none;
}

.aceleres-selection-delete-layer {
  position: fixed;
  inset: 0;
  z-index: 2147483600;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.aceleres-selection-delete-backdrop {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
  background: rgba(0, 0, 0, 0.36);
  backdrop-filter: blur(2px);
}

.aceleres-selection-delete-dialog {
  position: relative;
  width: 100%;
  max-width: 420px;
  overflow: hidden;
  border: 1px solid var(--color-border, #e2e8f0);
  border-radius: 18px;
  color: var(--color-text, #1f2937);
  background: var(--color-background, #ffffff);
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.24);
}

.aceleres-selection-delete-content {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding: 22px 20px 18px;
}

.aceleres-selection-delete-icon {
  display: inline-flex;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #dc2626;
  background: #fee2e2;
  font-size: 20px;
}

.aceleres-selection-delete-content h3 {
  margin: 1px 0 5px;
  font-size: 16px;
  font-weight: 650;
  line-height: 1.35;
}

.aceleres-selection-delete-content p {
  margin: 0;
  color: #64748b;
  font-size: 14px;
  line-height: 1.45;
}

.aceleres-selection-delete-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid rgba(15, 23, 42, 0.08);
  background: #f8fafc;
}

.aceleres-selection-delete-actions button {
  display: inline-flex;
  min-height: 40px;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 0 15px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 10px;
  color: #334155;
  background: #ffffff;
  font-size: 14px;
  font-weight: 600;
}

.aceleres-selection-delete-actions button.is-danger {
  border-color: #dc2626;
  color: #ffffff;
  background: #dc2626;
}

.aceleres-selection-delete-actions button:disabled {
  cursor: wait;
  opacity: 0.58;
}

.aceleres-selection-delete-modal-enter-active,
.aceleres-selection-delete-modal-leave-active {
  transition: opacity 140ms ease;
}

.aceleres-selection-delete-modal-enter-from,
.aceleres-selection-delete-modal-leave-to {
  opacity: 0;
}

@media (max-width: 767px) {
  .acl-message-selection-footer {
    display: none !important;
  }

  .aceleres-mobile-message-selection-header {
    position: fixed;
    top: 0;
    right: 0;
    left: 0;
    z-index: 2147482500;
    display: block;
    height: calc(56px + env(safe-area-inset-top));
    padding-top: env(safe-area-inset-top);
    border-bottom: 1px solid rgba(15, 23, 42, 0.08);
    color: #1f2937;
    background: rgba(248, 246, 242, 0.98);
    box-shadow: 0 1px 4px rgba(15, 23, 42, 0.12);
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
  }

  .aceleres-mobile-message-selection-bar {
    position: relative;
    z-index: 2;
    display: flex;
    width: 100%;
    height: 56px;
    align-items: center;
    gap: 0;
    padding: 0 4px;
  }

  .aceleres-mobile-message-selection-button {
    display: inline-flex;
    width: 42px;
    height: 48px;
    min-width: 42px;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    border-radius: 999px;
    color: inherit;
    background: transparent;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }

  .aceleres-mobile-message-selection-button:active {
    background: rgba(15, 23, 42, 0.08);
  }

  .aceleres-mobile-message-selection-button:disabled {
    opacity: 0.36;
  }

  .aceleres-mobile-message-selection-count {
    min-width: 34px;
    padding-left: 4px;
    font-size: 19px;
    font-weight: 500;
    line-height: 1;
  }

  .aceleres-mobile-message-selection-spacer {
    flex: 1 1 auto;
    min-width: 0;
  }

  .aceleres-mobile-message-selection-backdrop {
    position: fixed;
    inset: 0;
    z-index: 1;
    width: 100%;
    height: 100%;
    border: 0;
    background: transparent;
  }

  .aceleres-mobile-message-selection-menu {
    position: absolute;
    top: 51px;
    right: 7px;
    z-index: 3;
    min-width: 210px;
    overflow: hidden;
    padding: 6px;
    border: 1px solid rgba(15, 23, 42, 0.08);
    border-radius: 12px;
    background: #ffffff;
    box-shadow: 0 10px 28px rgba(15, 23, 42, 0.2);
  }

  .aceleres-mobile-message-selection-menu button {
    display: flex;
    width: 100%;
    min-height: 46px;
    align-items: center;
    gap: 12px;
    padding: 0 12px;
    border: 0;
    border-radius: 8px;
    color: #1f2937;
    background: transparent;
    font-size: 15px;
    text-align: left;
  }

  .aceleres-mobile-message-selection-menu button:active {
    background: rgba(15, 23, 42, 0.07);
  }

  .aceleres-mobile-message-selection-menu button:disabled {
    opacity: 0.4;
  }

  :global(html.aceleres-flutter-app) .aceleres-mobile-message-selection-header {
    height: 56px;
    padding-top: 0;
  }

  .aceleres-mobile-selection-header-enter-active,
  .aceleres-mobile-selection-header-leave-active {
    transition:
      opacity 130ms ease,
      transform 160ms cubic-bezier(0.16, 1, 0.3, 1);
  }

  .aceleres-mobile-selection-header-enter-from,
  .aceleres-mobile-selection-header-leave-to {
    opacity: 0;
    transform: translateY(-8px);
  }
}

:global(.dark) .aceleres-mobile-message-selection-header,
:global(.dark) .aceleres-mobile-message-selection-menu,
:global(.dark) .aceleres-selection-delete-dialog {
  color: #f1f5f9;
  background: #202c33;
}

:global(.dark) .aceleres-selection-delete-actions {
  border-color: rgba(255, 255, 255, 0.08);
  background: #17212b;
}

:global(.dark) .aceleres-selection-delete-actions button {
  border-color: rgba(255, 255, 255, 0.12);
  color: #f1f5f9;
  background: #202c33;
}

:global(.dark) .aceleres-selection-delete-actions button.is-danger {
  border-color: #dc2626;
  color: #ffffff;
  background: #dc2626;
}

@media (prefers-reduced-motion: reduce) {
  .aceleres-mobile-selection-header-enter-active,
  .aceleres-mobile-selection-header-leave-active,
  .aceleres-selection-delete-modal-enter-active,
  .aceleres-selection-delete-modal-leave-active {
    transition-duration: 0.01ms !important;
  }
}
</style>
